import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import type { HeaderNav } from 'interfaces/navigation/Header';
import type { FranchiseListSearch } from 'interfaces/content/Ranking';
import { setPianoConfig } from 'services/Piano';
import { setHeadData } from 'utils/metaDataUtils/headData';
import type { PageViewDataLayerObject } from 'services/Gtm/entities';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import type { ArcSpanPageConfig } from 'services/ArcSpan/entities';
import { setarcSpanPageConfig } from 'services/ArcSpan';
import getFooter from 'api/Footer/getFooter';
import { getFranchiseYearSearch } from 'api/Ranking/getFranchiseListItems';
import setFranchisePageAdConfig from 'services/Ad/config/setFranchisePageAdConfig';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import NotFoundError from 'interfaces/errors/NotFoundError';
import getMenu from 'api/Header/getMenu';
import type { PreviewData } from 'interfaces/PreviewData';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import dynamic from 'next/dynamic';
import { PianoPaywallPageType } from '@/app_lib/types/piano/Paywall';

const RankingSearchPage = dynamic(() => import('pages/RankingSearch/RankingSearchPage'));
const Error404 = dynamic(() => import('components/Error404'));

interface FranchiseSearchProps {
  franchiseList: FranchiseListSearch;
  years: string[];
  initialYear: string;
}

const FranchiseSearch: NextPage<FranchiseSearchProps> = ({
  franchiseList,
  years,
  initialYear,
}: FranchiseSearchProps) => {
  const router = useRouter();
  const [initialSearch, setInitialSearch] = useState('');
  const [initialFilters, setInitialFilters] = useState<{ [key: string]: string | boolean }>({});

  useEffect(() => {
    const queryString = new URLSearchParams(router.asPath.split('?')[1] || '');

    setInitialSearch(queryString.get('Name') || '');
    queryString.delete('Name');

    const filters: { [key: string]: string | boolean } = {};
    queryString.forEach((value, key) => {
      if (value === 'true' || value === 'false') {
        filters[key] = value === 'true';
      } else {
        filters[key] = value;
      }
    });
    setInitialFilters(filters);
  }, [franchiseList]);

  return franchiseList.listType === 'grid' ? (
    <RankingSearchPage
      franchiseList={franchiseList}
      years={years}
      initialFilters={initialFilters}
      initialYear={initialYear}
      initialSearch={initialSearch}
    />
  ) : (
    <Error404
      heading='Oops! Page not found'
      link='homepage'
      msg='Our apologies. It may have expired or there could be a typo.
          Search for what you are looking for or find what you need on our'
    />
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { franchiseSlug, year }: { franchiseSlug: string; year: string } = context.params as {
      franchiseSlug: string;
      year: string;
    };
    const { previewData } = context as { previewData: PreviewData };
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', 'own', null),
      fallbackData.headerNav,
    );
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const franchiseList = await getFranchiseYearSearch(franchiseSlug, year, previewData);
    const dataLayer: PageViewDataLayerObject = safe(() => setPageGtmConfig(franchiseList), fallbackData.dataLayer);
    const arcSpanPageConfig: ArcSpanPageConfig = safe(
      () => setarcSpanPageConfig(franchiseList),
      fallbackData.arcSpanPageConfig,
    );
    const pageAdConfig = safe(() => setFranchisePageAdConfig(franchiseList), fallbackData.pageAdConfig);
    const pianoPageConfig = safe(
      () => setPianoConfig({ pageData: franchiseList, pageType: PianoPaywallPageType.Ranking }),
      fallbackData.pianoPageConfig,
    );
    const headData = safe(() => setHeadData(franchiseList), fallbackData.headData);

    if (franchiseSlug === 'fortune500') pageAdConfig.topics = ['fortune500'];
    if (franchiseList.listType === 'list') {
      throw new NotFoundError(`Invalidate Grid Ranking search pages`);
    }

    return {
      props: {
        arcSpanPageConfig,
        dataLayer,
        footer,
        franchiseList,
        headData,
        headerNav: menu,
        initialYear: year,
        pageAdConfig,
        pianoPageConfig,
        years: franchiseList.years,
      },
      revalidate: 60, // In seconds
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export const getStaticPaths: GetStaticPaths = () => {
  if (process.env.TEST) {
    return {
      fallback: 'blocking',
      paths: [
        {
          params: {
            franchiseSlug: 'fortune500',
            year: '2022',
          },
        },
        {
          params: {
            franchiseSlug: 'global500',
            year: '2022',
          },
        },
        {
          params: {
            franchiseSlug: '100-fastest-growing-companies',
            year: '2021',
          },
        },
      ],
    };
  }

  return {
    fallback: 'blocking',
    paths: [],
  };
};

export default FranchiseSearch;
